/**
 * Store Settings Page
 *
 * Configure store settings including name, currency, payment methods, etc.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { languages, getLanguageDirection, type LanguageCode } from '../../i18n/languages';

interface Store {
  id: string;
  name: string;
  slug: string;
  description?: string;
  status: 'draft' | 'active' | 'archived';
  currency: string;
  tax_rate?: number;
  tax_enabled?: boolean;
  contact_email?: string;
  custom_domain?: string;
  settings?: Record<string, unknown>;
}

interface OutletContext {
  store: Store;
}

interface StoreSettings {
  store_name: string;
  store_description?: string;
  currency: string;
  locale: LanguageCode;
  tax_rate?: number;
  shipping_enabled: boolean;
  payment_methods: string[];
  notification_email?: string;
  custom_domain?: string;
  custom_domain_verified?: boolean;
}

const DEFAULT_SETTINGS: StoreSettings = {
  store_name: '',
  store_description: '',
  currency: 'USD',
  locale: 'en',
  tax_rate: 0,
  shipping_enabled: false,
  payment_methods: [],
  notification_email: '',
  custom_domain: '',
  custom_domain_verified: false,
};

// Custom Domain Section Component
interface DomainStatus {
  domain: string;
  cname_configured: boolean;
  cname_records: Array<{ type: string; name: string; content: string }>;
  resolves: boolean;
  error?: string;
}

interface DomainInfo {
  store_id: string;
  store_slug: string;
  custom_domain: string | null;
  verified: boolean;
  verified_at: string | null;
  dns_status: DomainStatus | null;
  instructions: {
    cname_target: string;
    steps: string[];
    example: { type: string; name: string; value: string };
  };
}

function CustomDomainSection({
  storeId,
  customDomain,
  customDomainVerified,
  onDomainChange,
  onVerificationComplete,
}: {
  storeId: string;
  customDomain: string;
  customDomainVerified: boolean;
  onDomainChange: (domain: string) => void;
  onVerificationComplete: (verified: boolean) => void;
}) {
  const [domainInfo, setDomainInfo] = useState<DomainInfo | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifySuccess, setVerifySuccess] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);

  // Load domain status
  const loadDomainStatus = useCallback(async () => {
    try {
      setChecking(true);
      const response = await api.get(`/api/ecommerce/stores/${storeId}/domain`);
      if (response.ok) {
        const data = await response.json() as DomainInfo;
        setDomainInfo(data);
      }
    } catch (err) {
      console.error('Failed to load domain status:', err);
    } finally {
      setChecking(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (customDomain) {
      loadDomainStatus();
    }
  }, [customDomain, loadDomainStatus]);

  // Verify domain
  const handleVerify = async () => {
    if (!customDomain) return;

    setVerifying(true);
    setVerifyError(null);
    setVerifySuccess(null);

    try {
      const response = await api.post(`/api/ecommerce/stores/${storeId}/domain/verify`);
      const data = await response.json() as { verified: boolean; error?: string; message?: string };

      if (data.verified) {
        setVerifySuccess(data.message || 'Domain verified successfully!');
        onVerificationComplete(true);
        loadDomainStatus();
      } else {
        setVerifyError(data.error || 'Verification failed. Please check your DNS settings.');
      }
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Remove domain
  const handleRemoveDomain = async () => {
    if (!confirm('Are you sure you want to remove this custom domain?')) return;

    try {
      const response = await api.delete(`/api/ecommerce/stores/${storeId}/domain`);
      if (response.ok) {
        onDomainChange('');
        onVerificationComplete(false);
        setDomainInfo(null);
        setVerifySuccess('Domain removed successfully');
        setTimeout(() => setVerifySuccess(null), 3000);
      }
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Failed to remove domain');
    }
  };

  const cnameTarget = domainInfo?.instructions?.cname_target || 'store.xtform.app';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          Custom Domain
        </h2>
        {customDomainVerified && (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Verified
          </span>
        )}
      </div>

      {/* Success/Error Messages */}
      {verifySuccess && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {verifySuccess}
        </div>
      )}
      {verifyError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>{verifyError}</div>
        </div>
      )}

      {/* Domain Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Your Domain
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400 text-sm">https://</span>
          <input
            type="text"
            value={customDomain}
            onChange={(e) => onDomainChange(e.target.value.toLowerCase().trim())}
            placeholder="shop.yourdomain.com"
            disabled={customDomainVerified}
            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
          />
          {customDomainVerified && (
            <button
              onClick={handleRemoveDomain}
              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm"
              title="Remove domain"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* DNS Status */}
      {customDomain && domainInfo?.dns_status && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">DNS Status</span>
            <button
              onClick={loadDomainStatus}
              disabled={checking}
              className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
            >
              <svg className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              {domainInfo.dns_status.cname_configured ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className={domainInfo.dns_status.cname_configured ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}>
                CNAME: {domainInfo.dns_status.cname_configured ? 'Configured correctly' : 'Not configured'}
              </span>
            </div>
            {domainInfo.dns_status.cname_records.length > 0 && (
              <div className="text-gray-500 dark:text-gray-400 text-xs pl-6">
                Points to: {domainInfo.dns_status.cname_records.map(r => r.content).join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Setup Instructions */}
      {customDomain && !customDomainVerified && (
        <div className="mb-4">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
          >
            <svg className={`w-4 h-4 transition-transform ${showInstructions ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showInstructions ? 'Hide' : 'Show'} setup instructions
          </button>

          {showInstructions && (
            <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Setup Instructions</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-400">
                <li>Go to your domain registrar's DNS settings</li>
                <li>Add a new CNAME record</li>
                <li>Set the Name/Host to your subdomain (e.g., "shop" or "store")</li>
                <li>Set the Value/Target to: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{cnameTarget}</code></li>
                <li>Save the record and wait for DNS propagation (up to 48 hours)</li>
                <li>Click "Verify Domain" below to confirm</li>
              </ol>

              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Example DNS Record:</div>
                <div className="grid grid-cols-3 gap-2 text-sm font-mono">
                  <div>
                    <div className="text-gray-500 text-xs">Type</div>
                    <div className="text-gray-900 dark:text-white">CNAME</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Name</div>
                    <div className="text-gray-900 dark:text-white">{customDomain.split('.')[0] || 'shop'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Value</div>
                    <div className="text-gray-900 dark:text-white">{cnameTarget}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        {customDomain && !customDomainVerified && (
          <button
            onClick={handleVerify}
            disabled={verifying || !customDomain}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {verifying ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Verify Domain
              </>
            )}
          </button>
        )}

        {customDomainVerified && customDomain && (
          <a
            href={`https://${customDomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Visit Store
          </a>
        )}
      </div>

      {/* Help text */}
      <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        Use a custom domain like shop.yourdomain.com for your store. You'll need access to your domain's DNS settings.
      </p>
    </div>
  );
}

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
];


export default function StoreSettingsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;

  const [settings, setSettings] = useState<StoreSettings>(DEFAULT_SETTINGS);
  const [storeStatus, setStoreStatus] = useState<'draft' | 'active' | 'archived'>('draft');
  const [storeSlug, setStoreSlug] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const loadSettings = useCallback(async () => {
    const id = store?.id || storeId;
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/stores/${id}`);
      const data = await response.json() as { store: Store & { locale?: string } };
      if (data.store) {
        // Map store data to settings format
        setSettings({
          ...DEFAULT_SETTINGS,
          store_name: data.store.name || '',
          store_description: data.store.description || '',
          currency: data.store.currency || 'USD',
          locale: (data.store.locale as LanguageCode) || 'en',
          tax_rate: data.store.tax_rate || 0,
          notification_email: data.store.contact_email || '',
          custom_domain: data.store.custom_domain || '',
          // Extract payment methods and shipping from settings JSON if available
          shipping_enabled: Boolean((data.store.settings as Record<string, unknown>)?.shipping_enabled),
          payment_methods: ((data.store.settings as Record<string, unknown>)?.payment_methods as string[]) || [],
        });
        setStoreStatus(data.store.status || 'draft');
        setStoreSlug(data.store.slug || '');
      }
    } catch {
      // Store might not exist, use defaults
    } finally {
      setLoading(false);
    }
  }, [store?.id, storeId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    const id = store?.id || storeId;
    if (!id) return;

    // Validate tax rate
    const taxRate = settings.tax_rate || 0;
    if (taxRate < 0 || taxRate > 100) {
      setError('Tax rate must be between 0 and 100');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await api.put(`/api/ecommerce/stores/${id}`, {
        name: settings.store_name,
        description: settings.store_description,
        currency: settings.currency,
        locale: settings.locale,
        tax_rate: settings.tax_rate,
        contact_email: settings.notification_email,
        custom_domain: settings.custom_domain,
        settings: JSON.stringify({
          shipping_enabled: settings.shipping_enabled,
          payment_methods: settings.payment_methods,
        }),
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async () => {
    const id = store?.id || storeId;
    if (!id) return;

    const newStatus = storeStatus === 'active' ? 'draft' : 'active';

    try {
      setPublishing(true);
      setError(null);
      await api.put(`/api/ecommerce/stores/${id}`, {
        status: newStatus,
      });
      setStoreStatus(newStatus);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update store status');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Store Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Configure your e-commerce store settings
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          Settings saved successfully!
        </div>
      )}

      <div className="space-y-6">
        {/* Store Status / Publish */}
        <div className={`rounded-xl border p-6 ${
          storeStatus === 'active'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                storeStatus === 'active'
                  ? 'bg-green-100 dark:bg-green-900/50'
                  : 'bg-yellow-100 dark:bg-yellow-900/50'
              }`}>
                {storeStatus === 'active' ? (
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${
                  storeStatus === 'active'
                    ? 'text-green-900 dark:text-green-100'
                    : 'text-yellow-900 dark:text-yellow-100'
                }`}>
                  {storeStatus === 'active' ? 'Store is Live' : 'Store is in Draft Mode'}
                </h2>
                <p className={`text-sm ${
                  storeStatus === 'active'
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-yellow-700 dark:text-yellow-300'
                }`}>
                  {storeStatus === 'active'
                    ? `Your store is publicly accessible at /s/${storeSlug}`
                    : 'Your store is not visible to customers. Publish it to make it live.'}
                </p>
              </div>
            </div>
            <button
              onClick={handlePublishToggle}
              disabled={publishing}
              className={`px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                storeStatus === 'active'
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {publishing ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {storeStatus === 'active' ? 'Unpublishing...' : 'Publishing...'}
                </>
              ) : storeStatus === 'active' ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  Unpublish Store
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Publish Store
                </>
              )}
            </button>
          </div>
          {storeStatus === 'active' && storeSlug && (
            <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <span className="text-sm text-green-700 dark:text-green-300">Store URL:</span>
                <a
                  href={`/s/${storeSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-600 dark:text-green-400 hover:underline font-medium flex items-center gap-1"
                >
                  {window.location.origin}/s/{storeSlug}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          )}
        </div>
        {/* General Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            General Settings
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Store Name
              </label>
              <input
                type="text"
                value={settings.store_name}
                onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
                placeholder="My Store"
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Store Description
              </label>
              <textarea
                value={settings.store_description || ''}
                onChange={(e) => setSettings({ ...settings, store_description: e.target.value })}
                placeholder="Describe your store..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notification Email
              </label>
              <input
                type="email"
                value={settings.notification_email || ''}
                onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                placeholder="orders@mystore.com"
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Receive order notifications at this email address
              </p>
            </div>
          </div>
        </div>

        {/* Language & Region */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Language & Region
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Store Language
              </label>
              <select
                value={settings.locale}
                onChange={(e) => setSettings({ ...settings, locale: e.target.value as LanguageCode })}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {Object.entries(languages).map(([code, lang]) => (
                  <option key={code} value={code}>
                    {lang.nativeName} ({lang.name})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This sets the language for your public storefront
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Text Direction
              </label>
              <div className="flex items-center gap-3 h-[42px]">
                <div className={`px-4 py-2 rounded-lg border ${
                  getLanguageDirection(settings.locale) === 'ltr'
                    ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                }`}>
                  <span className="text-sm font-medium">LTR</span>
                  <span className="text-xs ml-1">(Left to Right)</span>
                </div>
                <div className={`px-4 py-2 rounded-lg border ${
                  getLanguageDirection(settings.locale) === 'rtl'
                    ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                }`}>
                  <span className="text-sm font-medium">RTL</span>
                  <span className="text-xs ml-1">(Right to Left)</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Automatically set based on the selected language
              </p>
            </div>
          </div>
        </div>

        {/* Currency & Tax */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Currency & Tax
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tax Rate (%)
              </label>
              <input
                type="number"
                value={settings.tax_rate || 0}
                onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) || 0 })}
                min="0"
                max="100"
                step="0.1"
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Shipping */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Shipping
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900 dark:text-white">
                Enable Shipping
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Allow customers to enter shipping addresses at checkout
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, shipping_enabled: !settings.shipping_enabled })}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings.shipping_enabled
                  ? 'bg-teal-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                  settings.shipping_enabled ? 'translate-x-7' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Payment Methods - Link to dedicated page */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Payment Methods
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Configure payment gateways like Stripe, PayPal, and more
              </p>
            </div>
            <Link
              to={`/store/${storeId}/payments`}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Configure Payments
            </Link>
          </div>
        </div>

        {/* Custom Domain */}
        <CustomDomainSection
          storeId={(store?.id || storeId)!}
          customDomain={settings.custom_domain || ''}
          customDomainVerified={settings.custom_domain_verified || false}
          onDomainChange={(domain) => setSettings({ ...settings, custom_domain: domain })}
          onVerificationComplete={(verified) => setSettings({ ...settings, custom_domain_verified: verified })}
        />

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
